import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { motion } from 'framer-motion';
import { mockData } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const ContactPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '16px'
  };

  const center = mockData.companyInfo.location.coordinates;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/send`, formData);
      
      if (response.data.success) {
        toast({
          title: "Message Sent!",
          description: response.data.message || "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or email us directly.",
        variant: "destructive"
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero */}
      <motion.section 
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-900 to-blue-800 text-white"
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div variants={fadeInUp}>
            <Badge className="bg-white/20 text-white border-0 px-6 py-2 mb-6">{t('common.getInTouch')}</Badge>
          </motion.div>
          <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold mb-6">
            {t('contact.title')}
          </motion.h1>
          <motion.p variants={fadeInUp} className="text-2xl text-blue-100 font-light">
            {t('contact.subtitle')}
          </motion.p>
        </div>
      </motion.section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('contact.getInTouch')}</h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {t('contact.reachOut')}
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="space-y-6">
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  <Card className="p-6 border-2 hover:border-blue-500 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-4 rounded-xl">
                      <Phone className="w-7 h-7 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{t('contact.phone')}</h3>
                      <p className="text-gray-600 text-lg">{mockData.companyInfo.contact.phone}</p>
                      <p className="text-sm text-gray-500 mt-1">Mon-Fri: 8:00 AM - 6:00 PM</p>
                    </div>
                  </div>
                </Card>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="p-6 border-2 hover:border-blue-500 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-4 rounded-xl">
                      <Mail className="w-7 h-7 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-1">Email</h3>
                      <p className="text-gray-600 text-lg">{mockData.companyInfo.contact.email}</p>
                      <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                    </div>
                  </div>
                </Card>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                <Card className="p-6 border-2 hover:border-blue-500 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 p-4 rounded-xl">
                      <MapPin className="w-7 h-7 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{t('contact.location')}</h3>
                      <p className="text-gray-600 text-lg">{mockData.companyInfo.contact.address}</p>
                    </div>
                  </div>
                </Card>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInUp}
            >
              <Card className="p-8 shadow-xl border-2 border-blue-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">{t('contact.name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-900 focus:outline-none transition-colors text-lg"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">{t('contact.email')} *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-900 focus:outline-none transition-colors text-lg"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">{t('contact.subject')} *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-900 focus:outline-none transition-colors text-lg"
                    placeholder="Investment Inquiry"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">{t('contact.message')} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:border-blue-900 focus:outline-none transition-colors text-lg"
                    placeholder="Tell us about your investment interests..."
                  ></textarea>
                </div>
                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all group"
                  >
                    <Send className="mr-2" /> {t('contact.send')}
                  </Button>
                </motion.div>
              </form>
            </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
        className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Visit Our Office</h2>
            <p className="text-xl text-gray-600">Located in Kinshasa, the heart of DRC business</p>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <LoadScript googleMapsApiKey="">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={13}
              >
                <Marker position={center} />
              </GoogleMap>
            </LoadScript>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default ContactPage;
